<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import Icon from "@/components/Icon.vue";
import { mdiTagOutline, mdiClose } from "@mdi/js";
import { usePromo } from "@/globals/promo";

/* Whether a promo is running, and what it is worth, comes from the backend via
   globals/promo.js — the same answer checkout acts on. Nothing in this file
   hardcodes a deadline or an amount. */
const { promo, isActive, label } = usePromo();

const store = useStore();
const isLoggedIn = computed(() => Boolean(store.state.auth?.status?.loggedIn));

/* The promo's value and end date are the key on purpose. A bare
   "promo-dismissed" flag would be permanent — everyone who dismissed this promo
   would silently never see the *next* one either. Keying on what the promo is
   means a new one starts from a clean slate, and the stale keys are harmless. */
const storageKey = computed(
  () =>
    `promo-dismissed-${promo.percentOff ?? promo.amountOff}-${promo.endsOn ?? "open"}`
);

// localStorage throws in Safari private mode and when cookies are blocked.
// A promo bar is never worth breaking the app root over, so both directions
// fail soft.
const readDismissed = () => {
  try {
    return localStorage.getItem(storageKey.value) === "1";
  } catch {
    return false;
  }
};

/* Read once the promo has loaded — before that the key is not known yet. */
const dismissed = ref(false);
watch(
  () => promo.loaded,
  (loaded) => {
    if (loaded) dismissed.value = readDismissed();
  },
  { immediate: true }
);

const isVisible = computed(() => isActive.value && !dismissed.value);

/*
 * Publishes the bar's real height as `--promo-height` on <html>.
 *
 * The bar sits in normal document flow, so anything else in flow is pushed down
 * for free. `position: fixed` elements are not — they are placed against the
 * viewport and ignore it entirely. The auth screens put their Back link, the
 * language switcher and the theme toggle at `fixed top-5`, so the bar landed
 * underneath all three.
 *
 * Measured rather than hardcoded because the height is not a constant: the
 * sentence wraps to two lines on a narrow screen, and a guessed 40px would be
 * wrong exactly where the overlap is worst. Anything that needs to clear the
 * bar reads the variable (see `.below-promo` in _app.css), so it stays correct
 * when the bar wraps, is dismissed, or expires.
 */
const barEl = ref(null);
let observer = null;

const publishHeight = () => {
  const px = isVisible.value && barEl.value ? barEl.value.offsetHeight : 0;
  document.documentElement.style.setProperty("--promo-height", `${px}px`);
};

onMounted(() => {
  publishHeight();
  window.addEventListener("resize", publishHeight);
});

/* The bar appears only once the promo request answers, so it is almost never in
   the DOM at mount. Observe it whenever it is (re)inserted rather than once. */
watch(barEl, (el) => {
  observer?.disconnect();
  observer = null;
  // The sentence rewraps on resize and on a font swap, neither of which fires
  // anything else useful.
  if (el && typeof ResizeObserver !== "undefined") {
    observer = new ResizeObserver(publishHeight);
    observer.observe(el);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  window.removeEventListener("resize", publishHeight);
  // Leaving a stale height behind would push every fixed overlay down by the
  // height of a bar that is no longer on screen.
  document.documentElement.style.setProperty("--promo-height", "0px");
});

watch(isVisible, () => {
  // After the v-if has actually removed or inserted the node.
  requestAnimationFrame(publishHeight);
});

const dismiss = () => {
  dismissed.value = true;
  try {
    localStorage.setItem(storageKey.value, "1");
  } catch {
    // Dismissal survives only this page load. Acceptable.
  }
};
</script>

<template>
  <!-- Placement: normal document flow, rendered above <router-view> in App.vue.
       Deliberately not `fixed`. App.vue writes document.body.style.paddingLeft /
       paddingRight to reserve the sidebar's width, and body already carries the
       pt-14 that clears the fixed NavBar — an in-flow strip inherits both, so it
       lands neatly under the navbar and beside the sidebar without a single
       hardcoded offset, and it pushes page content down instead of covering it.
       A fixed full-width bar would need to duplicate that sidebar arithmetic
       (including the RTL flip) and would sit on top of the first row of every
       page. -->
  <aside
    v-if="isVisible"
    ref="barEl"
    aria-label="Current promotion"
    class="promo-bar relative"
  >
    <div
      class="relative mx-auto flex max-w-[1600px] items-center justify-center gap-2 py-2 pl-4 pr-10 sm:px-12"
    >
      <icon
        :path="mdiTagOutline"
        :size="16"
        w="w-4"
        h="h-4"
        class="promo-icon flex-shrink-0"
      />

      <!-- No code to copy: the payment page applies the discount on its own,
           and a code shown here could not be entered anyway — Stripe does not
           offer the promo-code box while a discount is already applied.
           "Payment page", not "checkout": in this panel /checkout is the token
           cart, which spends tokens and never sees the promo. The discount lands
           when tokens are bought, which is what the sentence has to point at. -->
      <p class="promo-text text-center text-xs leading-5 sm:text-sm">
        <strong class="font-bold">{{
          label ? `${label} every token purchase` : "Discount on every token purchase"
        }}</strong> — applied
        on the payment page, no code needed.
        <!-- Logged-out visitors go to sign-up: /products sits behind the login
             guard and would bounce them to a sign-in form instead.
             No focus:outline-none here: _brand-system.css draws a global
             :focus-visible outline, and suppressing it would leave keyboard
             users with no indication of where they are. -->
        <router-link
          :to="isLoggedIn ? '/products' : '/register'"
          class="promo-link"
          >{{ isLoggedIn ? "Buy tokens" : "Sign up" }}</router-link
        >
      </p>
    </div>

    <!-- Absolutely positioned so the sentence stays optically centred in the
         strip rather than being pushed left by the button's width. -->
    <button
      type="button"
      aria-label="Dismiss promotion"
      class="promo-dismiss absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-1.5 sm:right-3"
      @click="dismiss"
    >
      <icon :path="mdiClose" :size="16" w="w-4" h="h-4" />
    </button>
  </aside>
</template>

<style scoped>
/*
 * A wave of the brand colour, deep to bright and back, and deliberately the
 * SAME band in light and dark mode: it is an announcement, not part of the UI,
 * and a variant per theme would let it recede into whichever one is active.
 *
 * Brand variables rather than fixed colours, so the band follows the colour a
 * user picks in Account → Appearance along with the rest of the panel.
 *
 * No stop is lighter than brand-600. White on 600 is 4.7:1 for any colour
 * brand-color.js generates, the other stops are darker, and a gradient never
 * blends lighter than its lighter end — so the sentence clears WCAG AA wherever
 * the drift has carried the wave, with no scrim or text shadow to prop it up.
 *
 * Written as a raw linear-gradient rather than Tailwind utilities because v2
 * supports only a single `via-` stop — seven stops is not expressible there.
 */
.promo-bar {
  /*
   * The final stop repeats the first on purpose. At `background-size: 200%` with
   * `repeat-x` the band tiles, and any difference between the two ends would
   * show up as a hard seam sliding through the bar once per cycle.
   */
  background-image: linear-gradient(
    90deg,
    rgb(var(--brand-950)) 0%,
    rgb(var(--brand-800)) 17%,
    rgb(var(--brand-600)) 33%,
    rgb(var(--brand-700)) 50%,
    rgb(var(--brand-600)) 67%,
    rgb(var(--brand-900)) 83%,
    rgb(var(--brand-950)) 100%
  );
  background-size: 200% 100%;
  background-repeat: repeat-x;
  border-bottom: 1px solid rgba(0, 0, 0, 0.25);

  /*
   * Slow enough that you never catch it moving — 40s for one pass means about
   * 5px a second. The point is that the bar looks alive when your eye returns to
   * it, not that anything appears to be happening. Anything under ~20s starts to
   * read as a loading bar, which would be a lie about what this is.
   *
   * `background-position` rather than translating a wider child: this element is
   * the page's first row and full-bleed, so a transformed child would need
   * overflow clipping and its own stacking context.
   */
  /* Both in one declaration: a second `.promo-bar { animation: … }` later in the
     file would replace this outright rather than adding to it. */
  animation: promo-bar-in 200ms ease-out, promo-drift 40s linear infinite;
}

@keyframes promo-drift {
  from {
    background-position: 0 0;
  }
  to {
    background-position: -200% 0;
  }
}

.promo-text {
  color: #fff;
}

.promo-icon {
  color: rgba(255, 255, 255, 0.85);
}

.promo-link {
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
  /* Tailwind 2.2 has no `underline-offset-*` utility — that landed in v3. */
  text-underline-offset: 2px;
}

.promo-link:hover {
  text-decoration: none;
}

.promo-dismiss {
  color: rgba(255, 255, 255, 0.7);
  transition: color 150ms ease, background-color 150ms ease;
}

.promo-dismiss:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.15);
}

/* The global :focus-visible outline is brand-500, which nearly disappears into
   a band made of the same colour. White reads on every stop. */
.promo-link:focus-visible,
.promo-dismiss:focus-visible {
  outline-color: #fff;
}

/* The strip appears after hydration, so without this it snaps into place and
   shoves the page down. The fade masks that reflow. Declared alongside the drift
   above — see the note there. */

@keyframes promo-bar-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .promo-bar {
    animation: none;
  }
}
</style>
